/**
 * /de/comparisons/trello — Draftlize vs Trello (German).
 * Targets trello kostenlose alternative (50) / trello alternative.
 * Links to /de/produkt-roadmap.
 */
import { MarketingShell } from '../MarketingShell';

export function TrelloDe() {
  return (
    <MarketingShell
      currentLocale="de"
      hreflangPath="/comparisons/trello"
      hreflangLocales={['de']}
      documentTitle="Trello-Alternative für Produktteams (kostenlos starten) · Draftlize"
      documentDescription="Trello-Boards zeigen Aufgaben, aber nicht das „Warum“. Eine Alternative, die Entscheidungen und Kontext mit der Arbeit verbindet — Draftlize. Der Vergleich."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Vergleich · vs Trello</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 58px)', lineHeight: 1.04, margin: '12px 0 24px' }}>
            Die Trello-Alternative,<br />
            <em>die den Kontext mitliefert.</em>
          </h1>
          <p className="lede">
            Trello ist ein wunderbar einfaches Kanban-Board: Karten, Listen, Drag-and-drop. Perfekt, um zu sehen, <em>was</em> zu tun ist. Nur zeigt eine Trello-Karte nicht das <em>Warum</em> dahinter – die Entscheidung, die sie ausgelöst hat, und was passiert, wenn diese sich ändert. Draftlize verbindet jede Aufgabe mit der Entscheidung und dem Kontext, aus dem sie stammt – und markiert „stale“, wenn sich die Grundlage verschiebt.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">Kurzfassung</span>
            <h2 className="serif">Direkt nebeneinander, <em>6 Dimensionen.</em></h2>
          </div>
          <p>Aufgaben-Board gegen Entscheidungs-Substrat – gut für Verschiedenes.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>Trello</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Zeigt</td><td className="ours yes">Entscheidung + Kontext</td><td className="partial">Aufgabe / Status</td></tr>
            <tr><td>Das „Warum“ hinter der Karte</td><td className="ours yes">verknüpft</td><td className="no">—</td></tr>
            <tr><td>Stale, wenn die Grundlage sich ändert</td><td className="ours yes">nativ</td><td className="no">—</td></tr>
            <tr><td>KI-Agent liest den Kontext</td><td className="ours yes">via MCP</td><td className="no">—</td></tr>
            <tr><td>Einfaches visuelles Kanban</td><td className="partial">kein Board-Fokus</td><td className="yes">exzellent</td></tr>
            <tr><td>Schnell startklar, sehr einfach</td><td className="partial">Denk-Werkzeug</td><td className="yes">exzellent</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Wo <em>Trello gewinnt.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Einfaches Kanban</h3>
            <p>Für ein schnelles visuelles Board – Aufgaben in Spalten schieben – ist Trello unschlagbar einfach. Draftlize ist kein Task-Board.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Sofort loslegen</h3>
            <p>Trello ist in Minuten eingerichtet, ohne Lernkurve. Draftlize ist ein Denk-Werkzeug für Produktentscheidungen, kein reines To-do.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Visuelle Übersicht</h3>
            <p>Wer den Status vieler Aufgaben auf einen Blick will, ist mit Trellos Board bestens bedient.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Wo <em>Draftlize gewinnt.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Das „Warum“ bleibt bei der Arbeit</h3>
            <p>Eine Trello-Karte sagt „mach X“. Eine Draftlize-Karte trägt die Entscheidung und den Grund für X mit – auffindbar, wenn jemand später fragt, warum.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Ändert sich die Grundlage, flaggt es</h3>
            <p>Kippt die Entscheidung hinter einer Aufgabe, wird die abhängige Karte „stale“. Kein stilles Weiterarbeiten an einer überholten Grundlage.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Für KI-gestützte Produktarbeit</h3>
            <p>Der Kontext hinter der Arbeit wird via MCP für Claude Code lesbar. Passt zur <a href="/de/produkt-roadmap" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>Produkt-Roadmap</a> darüber.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Trello zeigt, was zu tun ist. Draftlize behält, warum – und meldet, wenn das Warum nicht mehr gilt.
        </blockquote>
        <cite>Brauchst du nur ein Board, reicht Trello.</cite>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Gratis mit <em>$5 starten.</em></h2>
        </div>
        <div className="trust-strip">
          <span>$5 Gratis-Guthaben für neue Accounts</span>
          <span>Zahle nur, was du nutzt</span>
          <span>Guthaben verfällt nie</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Behalte Trello fürs Board. Nimm Draftlize für die Entscheidungen und den Kontext darunter – als Karten, die aktuell bleiben. $5 gratis, zahle nur, was du nutzt.
          </p>
          <a className="btn-primary" href="/">Gratis mit $5 starten</a>
        </div>
      </section>
    </MarketingShell>
  );
}
